import React from 'react';
import { fmtUsd } from '../../../utils/format';

/**
 * Where the average sits between the cheapest and most expensive price, as a 0–100
 * position on the range bar. Null when the range is missing or flat (every station
 * the same price), in which case the bar shows no marker rather than a fake one.
 * @param {number} avg
 * @param {number} low
 * @param {number} high
 * @returns {number|null}
 */
export const avgPosition = (avg, low, high) => {
  if (avg == null || low == null || high == null) return null;
  const a = Number(avg); const lo = Number(low); const hi = Number(high);
  if (![a, lo, hi].every(Number.isFinite) || hi <= lo) return null;
  return Math.min(100, Math.max(0, ((a - lo) / (hi - lo)) * 100));
};

/**
 * One waypoint on the Gas Price Summary: the average regular price, the
 * cheapest→most-expensive range (with the average marked on it), the station count
 * and diesel. The property's own waypoint is tinted brand blue and badged so it
 * stands apart from the corridors. Green/red on the range ends match the station
 * table below (cheap vs pricey for the guest).
 */
export default function WaypointFuelCard({ label, isProperty = false, avgRegular, cheapest, mostExpensive, stations, avgDiesel }) {
  const pos = avgPosition(avgRegular, cheapest, mostExpensive);
  return (
    <div
      data-testid="waypoint-fuel-card"
      data-property={isProperty ? 'true' : undefined}
      className={`flex flex-col rounded-lg border p-4 transition-shadow hover:shadow-sm ${
        isProperty
          ? 'border-[#32B1FF]/40 bg-[#32B1FF]/[0.06] dark:bg-[#32B1FF]/[0.08]'
          : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 truncate" title={label}>{label}</div>
          {isProperty && (
            <span className="mt-1 inline-flex rounded-full bg-[#32B1FF]/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#0B6FB0] dark:text-[#32B1FF]">
              Your property
            </span>
          )}
        </div>
        {stations != null && (
          <span className="shrink-0 rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:text-gray-300 tabular-nums">
            {Number(stations).toLocaleString()} {Number(stations) === 1 ? 'station' : 'stations'}
          </span>
        )}
      </div>

      <div className="mt-2 flex items-baseline gap-1">
        <span className={`text-2xl font-extrabold tabular-nums ${isProperty ? 'text-[#0B6FB0] dark:text-[#32B1FF]' : 'text-gray-900 dark:text-white'}`}>
          {fmtUsd(avgRegular, { cents: true })}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">/gal avg regular</span>
      </div>

      {cheapest != null && mostExpensive != null && (
        <div className="mt-3" data-testid="waypoint-range">
          <div className="relative h-1.5 rounded-full bg-gradient-to-r from-emerald-400/60 via-gray-200 to-red-400/60 dark:via-gray-700">
            {pos != null && (
              <span
                data-testid="waypoint-avg-marker"
                className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white dark:border-gray-900 bg-gray-900 dark:bg-white"
                style={{ left: `${pos}%` }}
                title={`Average ${fmtUsd(avgRegular, { cents: true })}`}
              />
            )}
          </div>
          <div className="mt-1.5 flex justify-between text-xs tabular-nums">
            <span className="text-emerald-600 dark:text-emerald-400">Cheapest {fmtUsd(cheapest, { cents: true })}</span>
            <span className="text-red-600 dark:text-red-400">Most {fmtUsd(mostExpensive, { cents: true })}</span>
          </div>
        </div>
      )}

      {avgDiesel != null && (
        <div className="mt-3 flex items-center justify-between border-t border-gray-100 dark:border-gray-800 pt-2 text-xs">
          <span className="text-gray-500 dark:text-gray-400">Diesel</span>
          <span className="font-semibold tabular-nums text-gray-700 dark:text-gray-200">{fmtUsd(avgDiesel, { cents: true })}</span>
        </div>
      )}
    </div>
  );
}
